import { useState, useCallback } from 'react';

interface FlashcardFlipCardProps {
  front: string;
  back: string;
  /** Compact sizing for browse lists; default false matches study session size. */
  compact?: boolean;
  className?: string;
}

const FlashcardFlipCard = ({ front, back, compact = false, className = '' }: FlashcardFlipCardProps) => {
  const [flipped, setFlipped] = useState(false);
  const [flipAnimEnabled, setFlipAnimEnabled] = useState(false);
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const use3DFlip = !reducedMotion;
  const minH = compact ? 'min-h-[160px] sm:min-h-[200px]' : 'min-h-[220px] sm:min-h-[280px]';

  const toggleFlip = useCallback(() => {
    setFlipAnimEnabled(true);
    setFlipped((was) => !was);
  }, []);

  if (use3DFlip) {
    return (
      <div className={`perspective-[1200px] w-full ${minH} ${className}`}>
        <button
          type="button"
          onClick={toggleFlip}
          className={`relative w-full ${minH} preserve-3d cursor-pointer touch-manipulation ${
            flipAnimEnabled ? 'transition-transform duration-500 ease-in-out' : ''
          } ${flipped ? '[transform:rotateY(180deg)]' : ''}`}
          style={{ transformStyle: 'preserve-3d' }}
          aria-label={flipped ? 'Show front' : 'Show back'}
        >
          <div
            className={`absolute inset-0 backface-hidden rounded-2xl shadow-md p-4 sm:p-6 flex flex-col items-center justify-center text-center bg-white border-2 border-indigo-100 ${minH}`}
            style={{ backfaceVisibility: 'hidden' }}
          >
            <p
              className={`font-bold text-gray-900 whitespace-pre-wrap ${
                compact ? 'text-base sm:text-lg' : 'text-xl sm:text-2xl'
              }`}
            >
              {front}
            </p>
            {!flipped && (
              <span className="absolute bottom-3 text-[10px] sm:text-xs font-bold text-indigo-400 uppercase tracking-wide">
                Tap to reveal
              </span>
            )}
          </div>
          <div
            className={`absolute inset-0 backface-hidden rounded-2xl shadow-md p-4 sm:p-6 flex flex-col items-center justify-center text-center bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 [transform:rotateY(180deg)] ${minH}`}
            style={{ backfaceVisibility: 'hidden' }}
          >
            <p
              className={`font-semibold text-gray-800 whitespace-pre-wrap ${
                compact ? 'text-sm sm:text-base' : 'text-lg sm:text-xl'
              }`}
            >
              {back}
            </p>
            {flipped && (
              <span className="absolute bottom-3 text-[10px] sm:text-xs font-bold text-indigo-500 uppercase tracking-wide">
                Tap for front
              </span>
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={toggleFlip}
      className={`relative w-full ${minH} cursor-pointer touch-manipulation ${className}`}
      aria-label={flipped ? 'Show front' : 'Show back'}
    >
      <div
        className={`absolute inset-0 rounded-2xl shadow-md p-4 sm:p-6 flex flex-col items-center justify-center text-center bg-white border-2 border-indigo-100 transition-opacity duration-300 ${
          flipped ? 'opacity-0 pointer-events-none' : 'opacity-100'
        } ${minH}`}
      >
        <p className="text-base sm:text-lg font-bold text-gray-900 whitespace-pre-wrap">{front}</p>
        {!flipped && (
          <span className="absolute bottom-3 text-[10px] font-bold text-indigo-400 uppercase">Tap to reveal</span>
        )}
      </div>
      <div
        className={`absolute inset-0 rounded-2xl shadow-md p-4 sm:p-6 flex flex-col items-center justify-center text-center bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 transition-opacity duration-300 ${
          flipped ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } ${minH}`}
      >
        <p className="text-sm sm:text-base font-semibold text-gray-800 whitespace-pre-wrap">{back}</p>
        {flipped && (
          <span className="absolute bottom-3 text-[10px] font-bold text-indigo-500 uppercase">Tap for front</span>
        )}
      </div>
    </button>
  );
};

export default FlashcardFlipCard;
